import { useFetch } from '../hooks/useFetch.js';
import { Spinner, Unknown } from './ui.jsx';

/**
 * Public reviews.
 *
 * Rendered identically on creator and brand pages. A marketplace that shows a
 * creator's rating but not the brand's quietly decides whose reputation counts,
 * and both sides here are accountable to the same information.
 */
export function Reviews({ subjectType, slug }) {
  const { data, loading } = useFetch(`/reviews/${subjectType}/${slug}`);

  if (loading) {
    return (
      <div className="py-6 text-ink-faint">
        <Spinner />
      </div>
    );
  }

  const reviews = data?.reviews ?? [];
  const summary = data?.summary ?? { ratingAvgBps: null, ratingCount: 0 };

  return (
    <section className="mt-10">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
          Reviews · after completed partnerships
        </h2>
        <p className="text-xs text-ink-faint">
          {subjectType === 'creator' ? 'Written by brands' : 'Written by creators'}. Public both
          ways.
        </p>
      </div>

      <div className="rounded-lg border border-rule bg-paper-raised">
        <div className="flex items-baseline gap-3 border-b border-rule px-4 py-3">
          <span className="text-2xl font-semibold tabular-nums">
            {summary.ratingAvgBps === null ? (
              <Unknown reason="No completed partnerships have been reviewed yet" />
            ) : (
              (summary.ratingAvgBps / 100).toFixed(1)
            )}
          </span>
          <span className="text-sm text-ink-muted">
            {summary.ratingCount === 0
              ? 'No reviews yet'
              : `from ${summary.ratingCount} review${summary.ratingCount === 1 ? '' : 's'}`}
          </span>
        </div>

        {reviews.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-muted">
            Reviews can only be written after a partnership completes, so a new profile has none.
            That is not a signal about quality.
          </p>
        ) : (
          <ul className="divide-y divide-rule">
            {reviews.map((r) => (
              <li key={r.id} className="px-4 py-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <Stars rating={r.rating} />
                  <span className="text-sm font-medium">
                    {r.author?.displayName ?? 'Former partner'}
                  </span>
                  <span className="text-xs text-ink-faint">
                    {r.authorRole === 'creator' ? 'creator' : 'brand'} ·{' '}
                    {new Date(r.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {r.body && <p className="mt-1.5 text-sm text-ink-muted">{r.body}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Stars({ rating }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          viewBox="0 0 20 20"
          className={`size-3.5 ${n <= rating ? 'text-caution' : 'text-rule-strong'}`}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L1.5 7.7l5.9-.9z" />
        </svg>
      ))}
    </span>
  );
}
